import React, { useState, useEffect } from 'react';
import { authService } from '../../services/auth';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const ProfileManagement = ({ refreshUser }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    shop_name: '',
    shop_address: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await authService.getProfile();
      setProfile(response);
      
      setFormData({
        first_name: response.first_name || '',
        last_name: response.last_name || '',
        email: response.email || '',
        phone_number: response.phone_number || '',
        shop_name: response.profile?.shop_name || '',
        shop_address: response.profile?.shop_address || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load profile data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setUpdating(true);
      await authService.updateProfile(formData);
      
      setAlert({
        type: 'success',
        message: 'Profile updated successfully!'
      });
      
      fetchProfile();
      
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to update profile. Please try again.'
      });
    } finally {
      setUpdating(false);
    }
  };

  const getVerificationBadge = (status) => {
    if (status === 'verified') {
      return (
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
          <i className="bi bi-check-circle-fill"></i>
          <span className="text-xs font-bold uppercase tracking-wider">Verified Account</span>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-3xl font-bold text-white tracking-tight mb-2">Profile Management</h2>
            <p className="text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>
              Manage your account settings and preferences
            </p>
          </div>
          {profile?.profile && getVerificationBadge(profile.profile.verification_status)}
        </div>

        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Form */}
          <div className="lg:col-span-2">
            <div className="rounded-2xl p-8" style={{ backgroundColor: '#161e35' }}>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <i className="bi bi-person-gear text-2xl" style={{ color: '#ef4444' }}></i>
                </div>
                <div>
                  <h5 className="text-xl font-bold text-white mb-1">Update Profile Information</h5>
                  <p className="text-sm" style={{ color: '#64748b' }}>Edit your personal and shop details</p>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-lg text-sm font-medium outline-none transition-all"
                      style={{
                        backgroundColor: '#0f1420',
                        color: '#f8fafc',
                        border: 'none'
                      }}
                      value={formData.first_name}
                      onChange={(e) => handleInputChange('first_name', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-lg text-sm font-medium outline-none transition-all"
                      style={{
                        backgroundColor: '#0f1420',
                        color: '#f8fafc',
                        border: 'none'
                      }}
                      value={formData.last_name}
                      onChange={(e) => handleInputChange('last_name', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 rounded-lg text-sm font-medium outline-none transition-all"
                      style={{
                        backgroundColor: '#0f1420',
                        color: '#f8fafc',
                        border: 'none'
                      }}
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-3 rounded-lg text-sm font-medium outline-none transition-all"
                      style={{
                        backgroundColor: '#0f1420',
                        color: '#f8fafc',
                        border: 'none'
                      }}
                      value={formData.phone_number}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Shop Name */}
                <div className="mb-6">
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                    Shop Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-lg text-sm font-medium outline-none transition-all"
                    style={{
                      backgroundColor: '#0f1420',
                      color: '#f8fafc',
                      border: 'none'
                    }}
                    value={formData.shop_name}
                    onChange={(e) => handleInputChange('shop_name', e.target.value)}
                    required
                  />
                </div>

                {/* Shop Address */}
                <div className="mb-8">
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                    Shop Address
                  </label>
                  <textarea
                    className="w-full px-4 py-3 rounded-lg text-sm font-medium outline-none transition-all resize-vertical"
                    style={{
                      backgroundColor: '#0f1420',
                      color: '#f8fafc',
                      border: 'none'
                    }}
                    rows="3"
                    value={formData.shop_address}
                    onChange={(e) => handleInputChange('shop_address', e.target.value)}
                    required
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="px-8 py-3 rounded-lg text-sm font-bold flex items-center gap-2 transition-all"
                    style={{
                      backgroundColor: '#ef4444',
                      color: '#fff'
                    }}
                    disabled={updating}
                    onMouseEnter={(e) => !updating && (e.currentTarget.style.backgroundColor = '#dc2626')}
                    onMouseLeave={(e) => !updating && (e.currentTarget.style.backgroundColor = '#ef4444')}
                  >
                    {updating ? (
                      <>
                        <span className="inline-block animate-spin">⏳</span>
                        Saving...
                      </>
                    ) : (
                      <>
                        Save Changes
                        <i className="bi bi-arrow-right"></i>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Account Information Card */}
            <div className="rounded-2xl p-6" style={{ backgroundColor: '#161e35' }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                  <i className="bi bi-person-circle text-lg" style={{ color: '#ef4444' }}></i>
                </div>
                <h6 className="text-base font-bold text-white">Account Information</h6>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                    Username
                  </label>
                  <div className="px-4 py-3 rounded-lg" style={{ backgroundColor: '#0f1420' }}>
                    <p className="text-white text-sm font-medium">{profile?.username}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                    User Type
                  </label>
                  <span className="inline-block px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                    Seller
                  </span>
                </div>
                
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                    Member Since
                  </label>
                  <div className="text-white text-sm font-medium">
                    {profile?.date_joined ? 
                      new Date(profile.date_joined).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      }) : 
                      'N/A'
                    }
                  </div>
                </div>

                {profile?.profile && (
                  <>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                        Credits
                      </label>
                      <div className="text-3xl font-bold" style={{ color: '#ef4444' }}>
                        {profile.profile.credits}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                        Verification Status
                      </label>
                      {profile.profile.verification_status === 'verified' ? (
                        <div className="flex items-center gap-2" style={{ color: '#10b981' }}>
                          <i className="bi bi-check-circle-fill"></i>
                          <span className="text-xs font-bold uppercase tracking-wider">Verified</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2" style={{ color: '#f59e0b' }}>
                          <i className="bi bi-clock-fill"></i>
                          <span className="text-xs font-bold uppercase tracking-wider">Pending</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                        Account Status
                      </label>
                      {profile.profile.is_account_active ? (
                        <div className="flex items-center gap-2" style={{ color: '#10b981' }}>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#10b981' }}></div>
                          <span className="text-xs font-bold uppercase tracking-wider">Active</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2" style={{ color: '#64748b' }}>
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#64748b' }}></div>
                          <span className="text-xs font-bold uppercase tracking-wider">Inactive</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Need Help Card */}
            <div className="rounded-2xl p-6 relative overflow-hidden" style={{ backgroundColor: '#ef4444' }}>
              <div className="relative z-10">
                <h6 className="text-lg font-bold text-white mb-2">Need Help?</h6>
                <p className="text-sm text-white opacity-90 mb-4">
                  Having trouble with your account settings? Our support team is available 24/7 to help you.
                </p>
                <button
                  className="w-full px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                  }}
                >
                  Contact Support
                </button>
              </div>
              <div className="absolute -bottom-4 -right-4 opacity-10">
                <i className="bi bi-headset" style={{ fontSize: '120px', color: '#fff' }}></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement;
